import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Github, ArrowRight, ArrowLeft, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import GitHubService from '../services/github';
import toast from 'react-hot-toast';

const OnboardingFlow = ({ onComplete, username, setUser }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [microsoftCalendarLoading, setMicrosoftCalendarLoading] = useState(false);
  const [error, setError] = useState(null);
  const shouldCompleteOnboarding = useRef(false);

  // Fetch user data and determine which steps are already completed
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get('/api/users/me');
        const user = response.data;
        
        setUserData(user);
        
        // Check which steps are already completed
        const completed = [];
        if (user.github_connected) {
          completed.push('github');
        }
        if (user.microsoft_calendar_connected) {
          completed.push('microsoft_calendar');
        }
        
        setCompletedSteps(completed);
        
        // Since all steps are optional, users can complete onboarding at any time
        // Find the first incomplete step and set it as current, or start from the beginning
        const firstIncompleteIndex = steps.findIndex(step => !completed.includes(step.id));
        if (firstIncompleteIndex !== -1) {
          setCurrentStep(firstIncompleteIndex);
        } else {
          // All steps completed, mark for completion after component mounts
          shouldCompleteOnboarding.current = true;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data. Please try again.');
        
        // If there's an error, try to continue with default state
        if (error.response?.status === 401) {
          // User is not authenticated, redirect to login
          navigate('/login');
          return;
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);

  // Check GitHub connection status when returning from OAuth
  useEffect(() => {
    const checkGitHubStatus = async () => {
      // Only check if we're on the GitHub step and it's not completed
      if (steps[currentStep]?.id === 'github' && !completedSteps.includes('github')) {
        try {
          const githubStatus = await GitHubService.getConnectionStatus();
          if (githubStatus.connected) {
            // GitHub was connected, mark as completed
            const newCompletedSteps = [...completedSteps, 'github'];
            setCompletedSteps(newCompletedSteps);
            
            // Since all steps are optional, users can complete onboarding at any time
            // Move to next step or allow completion
            if (newCompletedSteps.length === steps.length) {
              handleComplete();
            } else {
              // Move to next step
              setCurrentStep(currentStep + 1);
            }
          }
        } catch (error) {
          // GitHub not connected, continue normally
        }
      }
    };

    // Check after a short delay to allow for OAuth callback processing
    const timer = setTimeout(checkGitHubStatus, 1000);
    return () => clearTimeout(timer);
  }, [currentStep, completedSteps]);

  // Check Microsoft calendar connection status when returning from OAuth
  useEffect(() => {
    const checkMicrosoftCalendarStatus = async () => {
      // Only check if we're on the Microsoft calendar step and it's not completed
      if (steps[currentStep]?.id === 'microsoft_calendar' && !completedSteps.includes('microsoft_calendar')) {
        try {
          const response = await axios.get('/api/calendar/status');
          if (response.data.success && response.data.data.connected) {
            // Microsoft calendar was connected, mark as completed
            const newCompletedSteps = [...completedSteps, 'microsoft_calendar'];
            setCompletedSteps(newCompletedSteps);
            
            // Since all steps are optional, users can complete onboarding at any time
            // Move to next step or allow completion
            if (newCompletedSteps.length === steps.length) {
              handleComplete();
            } else {
              // Move to next step
              setCurrentStep(currentStep + 1);
            }
          }
        } catch (error) {
          // Microsoft calendar not connected, continue normally
        }
      }
    };

    // Check after a short delay to allow for OAuth callback processing
    const timer = setTimeout(checkMicrosoftCalendarStatus, 1000);
    return () => clearTimeout(timer);
  }, [currentStep, completedSteps]);

  // Check if onboarding should be completed after component mounts
  useEffect(() => {
    if (shouldCompleteOnboarding.current) {
      handleComplete();
    }
  }, []);

  const steps = [
    {
      id: 'github',
      title: 'Connect GitHub',
      description: 'Link your GitHub account to track development time and commits.',
      icon: Github,
      color: 'purple',
      required: false,
      details: [
        'Connect your GitHub account',
        'Track development time automatically',
        'Link commits to time entries',
        'Monitor project progress'
      ]
    },

    {
      id: 'microsoft_calendar',
      title: 'Connect Outlook Calendar',
      description: 'Link your Microsoft Outlook calendar to sync meetings and events.',
      icon: Calendar,
      color: 'green',
      required: false,
      details: [
        'Connect your Microsoft account',
        'Sync calendar events automatically',
        'Track meeting time and schedules',
        'Integrate with time tracking'
      ]
    }
  ];

  const handleSetup = (stepId) => {
    // If step is already completed, don't navigate to it
    if (completedSteps.includes(stepId)) {
      return;
    }
    
    switch (stepId) {
      case 'github':
        handleGitHubSetup();
        break;
      case 'microsoft_calendar':
        handleMicrosoftCalendarSetup();
        break;
      default:
        break;
    }
  };

  const handleGitHubSetup = async () => {
    try {
      setGithubLoading(true);
      setError(null);
      
      // Check if GitHub App is configured
      if (!GitHubService.isAppConfigured()) {
        setError('GitHub App is not configured. Please contact your administrator.');
        toast.error('GitHub App is not configured. Please contact your administrator.');
        return;
      }

      // Check current GitHub App installation status
      try {
        const githubStatus = await GitHubService.getAppStatus();
        if (githubStatus.installed) {
          // Already installed, mark as completed
          const newCompletedSteps = [...completedSteps, 'github'];
          setCompletedSteps(newCompletedSteps);
          
          toast.success('GitHub App is already installed!');
          
          // Since all steps are optional, users can complete onboarding at any time
          // Move to next step or allow completion
          if (newCompletedSteps.length === steps.length) {
            handleComplete();
          } else {
            // Move to next step
            setCurrentStep(currentStep + 1);
          }
          return;
        }
      } catch (error) {
        // GitHub App not installed, continue with installation flow
      }

      // Show user that they're being redirected to GitHub App installation
      toast.success('Redirecting to GitHub App installation...');
      
      // Install GitHub App
      await GitHubService.installApp();
      
      // Note: The user will be redirected to GitHub App installation
      // When they return, they'll come back to the GitHub callback component
      // We need to handle the return flow properly
      
    } catch (error) {
      console.error('Failed to install GitHub App:', error);
      setError('Failed to install GitHub App: ' + (error.message || 'Unknown error'));
      toast.error('Failed to install GitHub App: ' + (error.message || 'Unknown error'));
    } finally {
      setGithubLoading(false);
    }
  };

  const handleMicrosoftCalendarSetup = async () => {
    try {
      setMicrosoftCalendarLoading(true);
      setError(null);
      
      // Check current Microsoft calendar connection status
      try {
        const response = await axios.get('/api/calendar/status');
        if (response.data.success && response.data.data.connected) {
          // Already connected, mark as completed
          const newCompletedSteps = [...completedSteps, 'microsoft_calendar'];
          setCompletedSteps(newCompletedSteps);
          
          toast.success('Microsoft Calendar is already connected!');
          
          // Since all steps are optional, users can complete onboarding at any time
          // Move to next step or allow completion
          if (newCompletedSteps.length === steps.length) {
            handleComplete();
          } else {
            // Move to next step
            setCurrentStep(currentStep + 1);
          }
          return;
        }
      } catch (error) {
        // Microsoft calendar not connected, continue with connection flow
      }

      // Show user that they're being redirected to Microsoft OAuth
      toast.success('Redirecting to Microsoft OAuth...');
      
      // Set flag to indicate user came from onboarding
      sessionStorage.setItem('from_onboarding', 'true');
      
      // Redirect to Microsoft OAuth
      const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID || 'your_client_id_here';
      const redirectUri = encodeURIComponent(`${window.location.origin}/auth/microsoft/callback`);
      const scope = encodeURIComponent('openid email profile Calendars.Read offline_access');
      
      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scope}&response_mode=query`;
      
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('Failed to connect Microsoft Calendar:', error);
      setError('Failed to connect Microsoft Calendar: ' + (error.message || 'Unknown error'));
      toast.error('Failed to connect Microsoft Calendar: ' + (error.message || 'Unknown error'));
    } finally {
      setMicrosoftCalendarLoading(false);
    }
  };

  const handleSkip = async (stepId) => {
    const newCompletedSteps = [...completedSteps, stepId];
    setCompletedSteps(newCompletedSteps);
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // This is the last step, complete onboarding
      await handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Since all steps are now optional, users can complete onboarding at any time
      // Call backend to mark onboarding as complete
      await axios.post('/api/users/complete-onboarding');
      
      // Refresh user data to get updated onboarding status
      try {
        const userResponse = await axios.get('/api/users/me');
        const userData = userResponse.data;
        
        // Update user state with the new onboarding status
        if (setUser) {
          setUser(userData);
        }
      } catch (refreshError) {
        console.error('Error refreshing user data:', refreshError);
        // Continue even if refresh fails
      }
    } catch (error) {
      console.error('OnboardingFlow - Error completing onboarding:', error);
      setError('Failed to complete onboarding. Please try again.');
      
      // Continue even if API call fails
    } finally {
      setLoading(false);
    }
    
    // Since all steps are optional, always call onComplete
    onComplete();
  };

  const getStepColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200'
    };
    return colors[color] || colors.blue;
  };

  const getStepButtonColor = (color) => {
    const colors = {
      blue: 'bg-blue-600 hover:bg-blue-700',
      green: 'bg-green-600 hover:bg-green-700',
      purple: 'bg-purple-600 hover:bg-purple-700'
    };
    return colors[color] || colors.blue;
  };

  if (loading && !userData) {
    return (
      <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your onboarding progress...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];
  const IconComponent = currentStepData.icon;

  return (
    <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-center mb-4">
            <div className={`w-16 h-16 ${getStepColor(currentStepData.color)} rounded-full flex items-center justify-center border-2`}>
              <IconComponent className="w-8 h-8" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 text-center">Welcome to Tick Sync!</h1>
          <p className="text-center text-gray-600 mt-2">Let's get you set up with the essential features</p>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Setup Progress</span>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">{completedSteps.length}/{steps.length} completed</span>
              <button
                onClick={handleComplete}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
              >
                Skip All & Continue
              </button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedSteps.length / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{currentStepData.title}</h2>
            <p className="text-gray-600 mb-4">{currentStepData.description}</p>
            
            <div className={`${getStepColor(currentStepData.color)} border rounded-lg p-4 mb-6`}>
              <div className="flex items-start">
                <IconComponent className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0`} />
                <div className="text-left">
                  <h3 className="text-sm font-medium mb-1">What you'll get:</h3>
                  <ul className="text-xs space-y-1">
                    {currentStepData.details.map((detail, index) => (
                      <li key={index}>• {detail}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {completedSteps.includes(currentStepData.id) ? (
              <div className="space-y-3">
                <button
                  disabled
                  className="w-full bg-green-100 text-green-700 py-3 px-4 rounded-lg font-medium flex items-center justify-center cursor-not-allowed"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {currentStepData.title} Completed
                </button>
                
                {/* Show Continue to Dashboard button since all steps are optional */}
                <button
                  onClick={handleComplete}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Continue to Dashboard
                </button>
                
                {currentStep < steps.length - 1 && (
                  <div className="flex space-x-3">
                    {currentStep > 0 && (
                      <button
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Previous Step
                      </button>
                    )}
                    
                    {currentStep < steps.length - 1 && (
                      <button
                        onClick={() => setCurrentStep(currentStep + 1)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center"
                      >
                        Continue to Next Step
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleSetup(currentStepData.id)}
                disabled={loading || (currentStepData.id === 'github' && githubLoading) || (currentStepData.id === 'microsoft_calendar' && microsoftCalendarLoading)}
                className={`w-full ${getStepButtonColor(currentStepData.color)} text-white py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center ${
                  (loading || (currentStepData.id === 'github' && githubLoading) || (currentStepData.id === 'microsoft_calendar' && microsoftCalendarLoading)) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
              >
                {(loading || (currentStepData.id === 'github' && githubLoading) || (currentStepData.id === 'microsoft_calendar' && microsoftCalendarLoading)) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {currentStepData.id === 'github' && githubLoading ? 'Connecting to GitHub...' : 
                     currentStepData.id === 'microsoft_calendar' && microsoftCalendarLoading ? 'Connecting to Microsoft...' : 'Setting up...'}
                  </>
                ) : (
                  <>
                    <IconComponent className="w-5 h-5 mr-2" />
                    Set Up {currentStepData.title}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            )}
            
            {!completedSteps.includes(currentStepData.id) && !currentStepData.required && (
              <button
                onClick={() => handleSkip(currentStepData.id)}
                disabled={loading || (currentStepData.id === 'github' && githubLoading) || (currentStepData.id === 'microsoft_calendar' && microsoftCalendarLoading)}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Clock className="w-5 h-5 mr-2" />
                Skip for Now
              </button>
            )}
            

          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              You can always set these up later from your Profile settings.
            </p>
          </div>

          {/* Step Indicators */}
          <div className="mt-6 flex justify-center space-x-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`w-3 h-3 rounded-full ${
                  completedSteps.includes(step.id) 
                    ? 'bg-green-500' 
                    : index === currentStep 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
